import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  applyMemberMentionSelection,
  CHAT_MEMBER_MENTION_DEBOUNCE_MS,
  ChatMemberMentionCandidate,
  filterMembersForMention,
  findActiveMemberMention,
  mentionSelectionCursorIndex,
} from '../utils/chatMemberMention.utils';

export function useChatMemberMention(
  members: ChatMemberMentionCandidate[],
  input: string,
  cursorIndex: number,
) {
  const activeMention = useMemo(
    () => findActiveMemberMention(input, cursorIndex),
    [cursorIndex, input],
  );

  const [debouncedMentionQuery, setDebouncedMentionQuery] = useState<string | null>(null);

  useEffect(() => {
    if (!activeMention?.query) {
      setDebouncedMentionQuery(null);

      return;
    }

    const timer = window.setTimeout(() => {
      setDebouncedMentionQuery(activeMention.query);
    }, CHAT_MEMBER_MENTION_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [activeMention?.query]);

  const showSuggestions = Boolean(
    activeMention &&
      activeMention.query.length > 0 &&
      debouncedMentionQuery === activeMention.query,
  );

  const suggestions = showSuggestions
    ? filterMembersForMention(members, debouncedMentionQuery ?? '')
    : [];

  const applyMention = useCallback(
    (member: ChatMemberMentionCandidate) => {
      if (!activeMention) {
        return { nextInput: input, nextCursorIndex: cursorIndex };
      }

      const nextInput = applyMemberMentionSelection(input, activeMention, member);
      const nextCursorIndex = mentionSelectionCursorIndex(activeMention, member);

      setDebouncedMentionQuery(null);

      return { nextInput, nextCursorIndex };
    },
    [activeMention, cursorIndex, input],
  );

  return {
    activeMention,
    suggestions,
    showSuggestions,
    applyMention,
  };
}
